import { useMemo } from "react"
import type { Capability } from "@csbi/contracts"
import { Menu, Tooltip } from "antd"
import type { MenuProps } from "antd"
import { Link, useLocation } from "react-router-dom"
import {
  Activity,
  BookOpen,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileBarChart,
  FileCheck,
  GitPullRequest,
  Grid3x3,
  History,
  KanbanSquare,
  LayoutGrid,
  HeartPulse,
  House,
  LayoutTemplate,
  ListChecks,
  Package,
  Scale,
  Send,
  Tags,
  TrendingDown,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react"
import type { MetricDomain } from "@csbi/metric-catalog"
import { DOMAIN_NAV, SITE_NAME, SITE_SUBTITLE } from "../../../shared/config/site.js"
import { domainCoverage, useCoverage } from "../../../features/coverage/use-coverage.js"

const DOMAIN_ICONS: Record<MetricDomain, LucideIcon> = {
  users: Users,
  activity: Activity,
  moduleUsage: LayoutGrid,
  products: Package,
  applications: FileCheck,
  packages: Wallet,
  submissions: Send,
  checklists: ListChecks,
  projects: KanbanSquare,
  changes: GitPullRequest,
  watchlists: Scale,
  tags: Tags,
  reportsUsage: FileBarChart,
  lifecycle: TrendingDown,
}

/*
 * A nav item names the capability it needs, and a person without it never sees the entry rather
 * than finding a page that answers 403. The same table decides here and at the route, so the two
 * cannot disagree.
 */
type NavItem = { path: string; label: string; icon: LucideIcon; requiresCapability?: Capability }
type NavGroup = { key: string; label: string; items: NavItem[] }

function buildGroups(): NavGroup[] {
  return [
    {
      key: "dashboards",
      label: "Dashboards",
      items: [
        { path: "/", label: "Home", icon: House },
        // The group is already called Dashboards, so the item cannot be too — same treatment as Metric Dictionary / All Metrics
        { path: "/dashboards", label: "My Dashboards", icon: LayoutTemplate },
      ],
    },
    {
      key: "domains",
      label: "Metric Domains",
      items: DOMAIN_NAV.map((d) => ({
        path: `/domain/${d.slug}`,
        label: d.label,
        icon: DOMAIN_ICONS[d.domain],
      })),
    },
    {
      key: "tenants",
      label: "Customers",
      items: [
        { path: "/tenants", label: "Deployments", icon: Building2 },
        { path: "/tenants/health", label: "Client Health", icon: HeartPulse },
      ],
    },
    { key: "catalog", label: "Metric Dictionary", items: [{ path: "/catalog", label: "All Metrics", icon: BookOpen }] },
    {
      key: "ops",
      label: "Data Health",
      items: [
        { path: "/ops/coverage", label: "Coverage Matrix", icon: Grid3x3 },
        { path: "/ops/runs", label: "Collection Runs", icon: History },
      ],
    },
    {
      key: "admin",
      label: "Administration",
      items: [{ path: "/admin/staff", label: "Staff", icon: Users, requiresCapability: "staff" }],
    },
  ]
}

export function matchLongestPrefix(pathname: string, paths: readonly string[]): string | undefined {
  let best: string | undefined
  for (const p of paths) {
    if (pathname !== p && !pathname.startsWith(`${p}/`)) continue
    if (!best || p.length > best.length) best = p
  }
  return best
}

export type AppSiderProps = {
  collapsed: boolean
  onToggle?: () => void
  capabilities?: readonly Capability[]
  onNavigate?: () => void
}

export function AppSider({ collapsed, onToggle, capabilities = [], onNavigate }: AppSiderProps) {
  const location = useLocation()
  const { data: coverage } = useCoverage()
  const perDomain = useMemo(() => domainCoverage(coverage), [coverage])

  const groups = useMemo(
    () =>
      buildGroups()
        .map((g) => ({ ...g, items: g.items.filter((i) => !i.requiresCapability || capabilities.includes(i.requiresCapability)) }))
        .filter((g) => g.items.length > 0),
    [capabilities],
  )
  const allPaths = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.path)), [groups])
  const selected = matchLongestPrefix(location.pathname, allPaths)

  const items: MenuProps["items"] = groups.map((group) => ({
    key: group.key,
    label: collapsed ? "" : group.label,
    type: "group",
    children: group.items.map((item) => {
      const domain = DOMAIN_NAV.find((d) => `/domain/${d.slug}` === item.path)
      const cov = domain ? perDomain.get(domain.domain) : undefined
      const Icon = item.icon
      const name = (
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
      )
      return {
        key: item.path,
        icon: <Icon size={15} strokeWidth={1.75} />,
        title: item.label,
        label: (
          <Link to={item.path} onClick={onNavigate} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {collapsed ? (
              name
            ) : (
              <Tooltip title={item.label} placement="right" mouseEnterDelay={0.4}>
                {name}
              </Tooltip>
            )}
            {cov && !collapsed && (
              <Tooltip title={`${cov.linked} of the ${cov.total} metrics in this domain are linked on at least one deployment`}>
                <span
                  style={{
                    fontSize: 11,
                    fontVariantNumeric: "tabular-nums",
                    color: cov.linked === cov.total ? "var(--csbi-status-good-ink)" : "var(--csbi-ink-muted)",
                  }}
                >
                  {cov.linked}/{cov.total}
                </span>
              </Tooltip>
            )}
          </Link>
        ),
      }
    }),
  }))

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: "var(--rd-nav-height, 64px)",
          padding: collapsed ? "0 16px" : "0 20px",
          borderBottom: "1px solid var(--csbi-gridline)",
          flex: "0 0 auto",
          overflow: "hidden",
        }}
      >
        <img
          className="csbi-logo"
          src="/image/logo.png"
          alt={SITE_NAME}
          height={20}
          // flexShrink: 0 together with width: auto is what stops it being squashed: a flex container compresses
          // shrinkable items, and a squashed <img> distorts proportionally rather than being cropped
          style={{ display: "block", width: "auto", flex: "0 0 auto" }}
        />
        {!collapsed && (
          <span
            title={SITE_SUBTITLE}
            style={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "0.05em",
              color: "var(--csbi-ink-muted)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {SITE_SUBTITLE}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Menu
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={selected ? [selected] : []}
          items={items}
          style={{ borderInlineEnd: "none" }}
        />
      </div>

      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 6,
            height: 40,
            flex: "0 0 auto",
            padding: "0 20px",
            border: "none",
            borderTop: "1px solid var(--csbi-gridline)",
            background: "transparent",
            color: "var(--csbi-ink-secondary)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          {!collapsed && "Collapse"}
        </button>
      )}
    </div>
  )
}
